import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventQuotesAPI, eventInvoicesAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, cn } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Calendar,
  Users,
  MapPin,
  Shield,
  Loader2,
  Eye,
  RefreshCw,
  DollarSign,
  FileText,
  Check,
  AlertCircle,
  Plus,
  Trash2,
  Clock,
  RotateCcw,
  Archive,
} from 'lucide-react';
import { toast } from 'sonner';

export default function EventQuotesManagePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [invoicesMap, setInvoicesMap] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Create Event Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEvent, setNewEvent] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    event_location: '',
    event_dates: '',
    event_duration: '',
    expected_attendees: '',
    event_type: '',
    coverage_level: 'standard',
    additional_notes: '',
    internal_notes: '',
  });
  
  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotesRes, invoicesRes, statsRes] = await Promise.all([
        eventQuotesAPI.getAll({}),
        eventInvoicesAPI.getAll({}),
        eventQuotesAPI.getStats(),
      ]);
      
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
      
      // Group invoices by quote_request_id
      const invMap = {};
      invoicesRes.data.forEach(inv => {
        if (!invMap[inv.quote_request_id]) {
          invMap[inv.quote_request_id] = { total: 0, paid: 0 };
        }
        invMap[inv.quote_request_id].total += inv.total || 0;
        invMap[inv.quote_request_id].paid += inv.amount_paid || 0;
      });
      setInvoicesMap(invMap);
    } catch (err) {
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  };

  const handleStatusChange = async (quoteId, newStatus, e) => {
    e.stopPropagation();
    try {
      await eventQuotesAPI.updateStatus(quoteId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Create new event handler
  const handleCreateEvent = async () => {
    if (!newEvent.business_name || !newEvent.contact_name || !newEvent.email || !newEvent.event_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreating(true);
    try {
      const response = await eventQuotesAPI.adminCreate(newEvent);
      toast.success(`Event created! Reference: ${response.data.reference_number}`);
      setCreateDialogOpen(false);
      setNewEvent({
        business_name: '',
        contact_name: '',
        email: '',
        phone: '',
        event_location: '',
        event_dates: '',
        event_duration: '',
        expected_attendees: '',
        event_type: '',
        coverage_level: 'standard',
        additional_notes: '',
        internal_notes: '',
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to create event: ' + (err.response?.data?.detail || err.message));
    }
    setCreating(false);
  };

  // Delete event handler
  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;
    
    setDeleting(true);
    try {
      await eventQuotesAPI.delete(eventToDelete.id, deletePermanent, true);
      toast.success(deletePermanent ? 'Event permanently deleted' : 'Event archived successfully');
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      setDeletePermanent(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete event: ' + (err.response?.data?.detail || err.message));
    }
    setDeleting(false);
  };

  // Restore event handler
  const handleRestoreEvent = async (quoteId, e) => {
    e.stopPropagation();
    try {
      await eventQuotesAPI.restore(quoteId);
      toast.success('Event restored successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to restore event');
    }
  };

  const openDeleteDialog = (quote, e) => {
    e.stopPropagation();
    setEventToDelete(quote);
    setDeletePermanent(false);
    setDeleteDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-600 border border-amber-200',
      reviewing: 'bg-blue-50 text-blue-600 border border-blue-200',
      quoted: 'bg-purple-50 text-purple-600 border border-purple-200',
      accepted: 'bg-emerald-50 text-emerald-600 border border-emerald-200',
      declined: 'bg-red-50 text-red-600 border border-red-200',
      archived: 'bg-slate-100 text-slate-500 border border-slate-200',
    };
    return styles[status] || 'bg-slate-100 text-slate-600 border border-slate-200';
  };

  const filteredQuotes = statusFilter === 'all' 
    ? quotes 
    : quotes.filter(q => q.status === statusFilter);

  if (user?.role !== 'admin' && user?.role !== 'physician' && user?.role !== 'clerk') {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 mx-auto mb-4 text-slate-500 opacity-20" />
        <h2 className="text-xl font-semibold mb-2">Access Required</h2>
        <p className="text-slate-500">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn p-2" data-testid="event-quotes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary mb-1">Event Management</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Event Quote Requests</h1>
          <p className="text-slate-500 mt-1">Manage event medical coverage requests and invoicing</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={fetchData} 
            className="gap-2 rounded-full bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          {user?.role === 'admin' && (
            <Button 
              onClick={() => setCreateDialogOpen(true)} 
              className="gap-2 rounded-full btn-primary-glow"
              data-testid="create-event-btn"
            >
              <Plus className="h-4 w-4" />
              Create New Event
            </Button>
          )}
        </div>
      </div>

      {/* Financial Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-blue-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.total_quotes}</p>
              <p className="text-xs text-slate-500">Total Quotes</p>
            </div>
          </div>
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.pending_quotes}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </div>
          </div>
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Check className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.approved_quotes}</p>
              <p className="text-xs text-slate-500">Approved</p>
            </div>
          </div>
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-purple-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">XCD {stats.total_invoiced?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-slate-500">Total Invoiced</p>
            </div>
          </div>
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Check className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-emerald-600">XCD {stats.total_paid?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-slate-500">Total Paid</p>
            </div>
          </div>
          <div className="premium-card-hover p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 text-orange-600" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-orange-600">XCD {stats.total_outstanding?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-slate-500">Outstanding</p>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-xl bg-white border-slate-200">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewing">Reviewing</SelectItem>
            <SelectItem value="quoted">Quoted</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Quotes Table */}
      <div className="premium-card overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">Quote Requests</h2>
          <p className="text-sm text-slate-500">{filteredQuotes.length} requests</p>
        </div>
        <div className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <p className="text-slate-500 text-sm">Loading quotes...</p>
              </div>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-slate-500" strokeWidth={1.5} />
              </div>
              <p className="text-slate-500">No quote requests found</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <table className="premium-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Reference</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Business</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Event</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Attendees</th>
                    <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Invoiced</th>
                    <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Paid</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((quote) => {
                    const invoiceData = invoicesMap[quote.id] || { total: 0, paid: 0 };
                    return (
                      <tr 
                        key={quote.id} 
                        className="border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => navigate(`/app/event-quotes/${quote.id}`)}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-mono bg-slate-50 px-2 py-1 rounded-lg text-primary">{quote.reference_number}</code>
                            {quote.source === 'admin' && (
                              <Badge className="text-[10px] bg-primary/10 text-primary border-primary/20">Admin</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div>
                            <p className="font-medium text-slate-900">{quote.business_name}</p>
                            <p className="text-xs text-slate-500">{quote.contact_name}</p>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div>
                            <p className="text-slate-900">{quote.event_type}</p>
                            <p className="text-xs text-slate-500">{quote.coverage_level}</p>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-slate-500">{quote.event_dates}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2 text-slate-900">
                            <Users className="h-4 w-4 text-slate-500" />
                            {quote.expected_attendees}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right">
                          {invoiceData.total > 0 ? (
                            <span className="font-medium text-slate-900">{invoiceData.total.toFixed(2)}</span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right">
                          {invoiceData.paid > 0 ? (
                            <span className="font-medium text-emerald-600">{invoiceData.paid.toFixed(2)}</span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <Select 
                            value={quote.status} 
                            onValueChange={(v) => handleStatusChange(quote.id, v, event)}
                          >
                            <SelectTrigger className={cn("w-28 h-8 text-xs rounded-lg", getStatusBadge(quote.status))}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200">
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="reviewing">Reviewing</SelectItem>
                              <SelectItem value="quoted">Quoted</SelectItem>
                              <SelectItem value="accepted">Accepted</SelectItem>
                              <SelectItem value="declined">Declined</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="rounded-xl hover:bg-slate-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/event-quotes/${quote.id}`);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {user?.role === 'admin' && (
                              <>
                                {quote.deleted_at ? (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="rounded-xl hover:bg-emerald-50 text-emerald-600"
                                    onClick={(e) => handleRestoreEvent(quote.id, e)}
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="rounded-xl hover:bg-red-50 text-red-600"
                                    onClick={(e) => openDeleteDialog(quote, e)}
                                    data-testid={`delete-event-${quote.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </div>
      </div>

      {/* Create Event Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Plus className="h-4 w-4 text-primary" />
              </div>
              Create New Event
            </DialogTitle>
            <DialogDescription>
              Create an event directly from the admin dashboard. This will send notification emails.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Business/Event Name *</Label>
                <Input
                  value={newEvent.business_name}
                  onChange={(e) => setNewEvent({ ...newEvent, business_name: e.target.value })}
                  placeholder="e.g., Caribbean Lean Events Ltd"
                  className="rounded-xl bg-white border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Contact Name *</Label>
                <Input
                  value={newEvent.contact_name}
                  onChange={(e) => setNewEvent({ ...newEvent, contact_name: e.target.value })}
                  placeholder="Primary contact person"
                  className="rounded-xl bg-white border-slate-200"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Email *</Label>
                <Input
                  type="email"
                  value={newEvent.email}
                  onChange={(e) => setNewEvent({ ...newEvent, email: e.target.value })}
                  placeholder="contact@example.com"
                  className="rounded-xl bg-white border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Phone</Label>
                <Input
                  value={newEvent.phone}
                  onChange={(e) => setNewEvent({ ...newEvent, phone: e.target.value })}
                  placeholder="+1 (758) xxx-xxxx"
                  className="rounded-xl bg-white border-slate-200"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Event Location *</Label>
              <Input
                value={newEvent.event_location}
                onChange={(e) => setNewEvent({ ...newEvent, event_location: e.target.value })}
                placeholder="Venue name and address"
                className="rounded-xl bg-white border-slate-200"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Event Date(s)</Label>
                <Input
                  value={newEvent.event_dates}
                  onChange={(e) => setNewEvent({ ...newEvent, event_dates: e.target.value })}
                  placeholder="e.g., March 15, 2026 or March 15-17, 2026"
                  className="rounded-xl bg-white border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Duration</Label>
                <Input
                  value={newEvent.event_duration}
                  onChange={(e) => setNewEvent({ ...newEvent, event_duration: e.target.value })}
                  placeholder="e.g., 8 hours, 3 days"
                  className="rounded-xl bg-white border-slate-200"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Expected Attendees *</Label>
                <Input
                  value={newEvent.expected_attendees}
                  onChange={(e) => setNewEvent({ ...newEvent, expected_attendees: e.target.value })}
                  placeholder="e.g., 500"
                  className="rounded-xl bg-white border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Event Type *</Label>
                <Select value={newEvent.event_type} onValueChange={(v) => setNewEvent({ ...newEvent, event_type: v })}>
                  <SelectTrigger className="rounded-xl bg-white border-slate-200">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="Concert / Festival">Concert / Festival</SelectItem>
                    <SelectItem value="Sports Event">Sports Event</SelectItem>
                    <SelectItem value="Corporate">Corporate</SelectItem>
                    <SelectItem value="Wedding / Private">Wedding / Private</SelectItem>
                    <SelectItem value="Community Event">Community Event</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Coverage Level</Label>
                <Select value={newEvent.coverage_level} onValueChange={(v) => setNewEvent({ ...newEvent, coverage_level: v })}>
                  <SelectTrigger className="rounded-xl bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Additional Notes (Client-Visible)</Label>
              <Textarea
                value={newEvent.additional_notes}
                onChange={(e) => setNewEvent({ ...newEvent, additional_notes: e.target.value })}
                placeholder="Any additional details about the event..."
                rows={2}
                className="rounded-xl bg-white border-slate-200"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Internal Notes (Admin Only)</Label>
              <Textarea
                value={newEvent.internal_notes}
                onChange={(e) => setNewEvent({ ...newEvent, internal_notes: e.target.value })}
                placeholder="Internal notes for staff only..."
                rows={2}
                className="rounded-xl bg-white border-slate-200"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEvent} 
              disabled={creating} 
              className="gap-2 rounded-full btn-primary-glow"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Event
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                <Archive className="h-4 w-4 text-red-600" />
              </div>
              {deletePermanent ? 'Permanently Delete Event?' : 'Archive Event?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  {deletePermanent 
                    ? 'This action cannot be undone. The event and all linked invoices will be permanently removed from the database.'
                    : 'The event will be archived and can be restored later. All linked invoices will also be archived.'}
                </p>
                {eventToDelete && (
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="font-medium text-slate-900">{eventToDelete.business_name}</p>
                    <p className="text-sm text-slate-500">Reference: {eventToDelete.reference_number}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="permanent-delete"
                    checked={deletePermanent}
                    onChange={(e) => setDeletePermanent(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <label htmlFor="permanent-delete" className="text-sm text-red-600 font-medium">
                    Permanently delete (cannot be undone)
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteEvent}
              disabled={deleting}
              className={cn(
                "rounded-full gap-2",
                deletePermanent ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"
              )}
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : deletePermanent ? (
                <Trash2 className="h-4 w-4" />
              ) : (
                <Archive className="h-4 w-4" />
              )}
              {deletePermanent ? 'Delete Permanently' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
